/**
 * CompanionSystem -- Manages companion summoning, dismissal, scaling,
 * and integration with combat and travel systems.
 *
 * Companions can be summoned from skills (grantsCompanion) or items
 * (companion-granting unique items). Each character may have at most
 * one companion, and the party may have at most 4 total.
 *
 * @module CompanionSystem
 */

import eventBus from '../core/EventBus.js';
import dataStore from './DataLoader.js';

/** Maximum companions per character. */
const MAX_COMPANIONS_PER_CHARACTER = 1;

/** Maximum total companions in party. */
const MAX_PARTY_COMPANIONS = 4;

class CompanionSystem {
  constructor() {
    /**
     * Active companions: Map<characterId, companionInstance>
     * @type {Map<string, object>}
     */
    this._activeCompanions = new Map();

    /**
     * Reference to party.
     * @type {object|null}
     */
    this._party = null;
  }

  /**
   * Initialize the companion system with a party reference.
   * @param {object} party
   */
  init(party) {
    this._party = party;
  }

  /**
   * Summon a companion for a character.
   * @param {object} character - Character entity
   * @param {string} companionId - ID from companions.json
   * @param {number} [level=1] - Scaling level (skill level or item level)
   * @returns {{ success: boolean, message: string, companion: object|null }}
   */
  summonCompanion(character, companionId, level = 1) {
    // Check per-character limit
    if (this._activeCompanions.has(character.id)) {
      return {
        success: false,
        message: `${character.name} already has a companion. Dismiss it first.`,
        companion: null,
      };
    }

    // Check party limit
    if (this._activeCompanions.size >= MAX_PARTY_COMPANIONS) {
      return {
        success: false,
        message: `Party companion limit (${MAX_PARTY_COMPANIONS}) reached.`,
        companion: null,
      };
    }

    // Look up companion definition
    const companionDef = dataStore.getById('companions', companionId);
    if (!companionDef) {
      return {
        success: false,
        message: `Companion definition not found: ${companionId}`,
        companion: null,
      };
    }

    // Create companion instance with scaled stats
    const companion = this._createCompanionInstance(companionDef, character, level);

    // Register
    this._activeCompanions.set(character.id, companion);

    // Sync to party
    this._syncToParty();

    eventBus.emit('companion:summoned', {
      character,
      companion,
    });

    return {
      success: true,
      message: `${character.name} summons ${companion.name}!`,
      companion,
    };
  }

  /**
   * Dismiss a character's companion.
   * @param {object} character
   * @returns {{ success: boolean, message: string }}
   */
  dismissCompanion(character) {
    const companion = this._activeCompanions.get(character.id);
    if (!companion) {
      return {
        success: false,
        message: `${character.name} has no active companion.`,
      };
    }

    this._activeCompanions.delete(character.id);
    this._syncToParty();

    eventBus.emit('companion:dismissed', {
      character,
      companion,
    });

    return {
      success: true,
      message: `${companion.name} has been dismissed.`,
    };
  }

  /**
   * Get the active companion for a character.
   * @param {object} character
   * @returns {object|null}
   */
  getCompanionForCharacter(character) {
    return this._activeCompanions.get(character.id) || null;
  }

  /**
   * Get all active party companions.
   * @returns {object[]}
   */
  getPartyCompanions() {
    return Array.from(this._activeCompanions.values());
  }

  /**
   * Get the owner character ID for a companion.
   * @param {object} companion
   * @returns {string|null}
   */
  getOwnerIdForCompanion(companion) {
    for (const [charId, comp] of this._activeCompanions) {
      if (comp === companion || comp.id === companion.id) {
        return charId;
      }
    }
    return null;
  }

  /**
   * Scale companion stats to a given level.
   * @param {object} companion - Active companion instance
   * @param {number} level - New scaling level
   */
  scaleCompanionStats(companion, level) {
    const def = dataStore.getById('companions', companion.definitionId);
    if (!def) return;

    const scaling = def.scalingPerLevel || { hp: 0, damage: 0, defense: 0, speed: 0 };
    const effectiveLevel = Math.max(1, level);

    companion.maxHp = def.stats.hp + scaling.hp * (effectiveLevel - 1);
    companion.hp = Math.min(companion.hp, companion.maxHp);
    companion.damage = def.stats.damage + scaling.damage * (effectiveLevel - 1);
    companion.defense = def.stats.defense + scaling.defense * (effectiveLevel - 1);
    companion.speed = def.stats.speed + scaling.speed * (effectiveLevel - 1);
    companion.level = effectiveLevel;
  }

  /**
   * Check skills for companion-granting skills and auto-summon.
   * Called when a skill is learned.
   * @param {object} character
   * @param {string} skillId
   * @param {number} skillLevel
   */
  checkSkillCompanion(character, skillId, skillLevel) {
    // Find the skill definition
    const trees = dataStore.getAll('skill_trees') || [];
    for (const tree of trees) {
      const skill = tree.skills.find(s => s.id === skillId);
      if (skill && skill.grantsCompanion) {
        const companionData = skill.grantsCompanion;
        // Find matching companion definition by sourceId
        const companions = dataStore.getAll('companions') || [];
        const def = companions.find(c => c.sourceId === skillId);
        if (def) {
          // Dismiss existing companion if any
          if (this._activeCompanions.has(character.id)) {
            this.dismissCompanion(character);
          }
          this.summonCompanion(character, def.id, skillLevel);
        }
        break;
      }
    }
  }

  /**
   * Check equipped item for companion-granting items and auto-summon.
   * @param {object} character
   * @param {object} item
   */
  checkItemCompanion(character, item) {
    if (!item || !item.fixedAffixes) return;

    const hasCompanionAffix = item.fixedAffixes.some(
      a => a.effect?.type === 'grantsCompanion'
    );
    if (!hasCompanionAffix) return;

    const affix = item.fixedAffixes.find(a => a.effect?.type === 'grantsCompanion');
    if (!affix) return;

    const companionId = affix.effect.companionId;
    const itemLevel = item.level || 1;

    // Dismiss existing companion if any
    if (this._activeCompanions.has(character.id)) {
      this.dismissCompanion(character);
    }
    this.summonCompanion(character, companionId, itemLevel);
  }

  /**
   * When an item is unequipped, dismiss its companion if applicable.
   * @param {object} character
   * @param {object} item
   */
  checkItemUnequip(character, item) {
    if (!item || !item.fixedAffixes) return;

    const hasCompanionAffix = item.fixedAffixes.some(
      a => a.effect?.type === 'grantsCompanion'
    );
    if (!hasCompanionAffix) return;

    const companion = this._activeCompanions.get(character.id);
    if (companion && companion.sourceType === 'item') {
      this.dismissCompanion(character);
    }
  }

  /**
   * Create a companion combatant for the combat system.
   * @param {object} companion - Active companion instance
   * @param {object} ownerCombatant - The owner's combatant in combat
   * @returns {object} combatant object for CombatSystem
   */
  createCompanionCombatant(companion, ownerCombatant) {
    return {
      id: `companion_${companion.id}_${ownerCombatant.id}`,
      name: companion.name,
      type: 'companion',
      hp: companion.hp,
      maxHp: companion.maxHp,
      mana: 0,
      maxMana: 0,
      ap: 5,
      maxAp: 5,
      attributes: {
        str: 10,
        agi: Math.floor(companion.speed / 1.5) + 5,
        int: 10,
        vit: 10,
        wis: 10,
        cha: 10,
      },
      initiative: 0,
      gridX: 0,
      gridY: 0,
      alive: true,
      statusEffects: [],
      character: null,
      behavior: companion.behavior || 'melee',
      defense: 10 + companion.defense,
      damage: companion.damage,
      speed: companion.speed,
      ownerId: ownerCombatant.id,
      companionData: companion,
    };
  }

  /**
   * Run companion AI during combat. Companion auto-acts on owner's turn.
   * @param {object} companionCombatant - The companion combatant
   * @param {object} combatSystem - Reference to CombatSystem
   * @returns {object[]} Array of actions taken
   */
  runCompanionAI(companionCombatant, combatSystem) {
    const actions = [];
    if (!companionCombatant.alive || companionCombatant.ap <= 0) return actions;

    // Give companion fresh AP for their auto-turn
    companionCombatant.ap = companionCombatant.maxAp;

    let maxIterations = 8;
    while (companionCombatant.ap > 0 && maxIterations-- > 0) {
      // Find targets (enemies of the player/companion side)
      const adjacentTargets = this._getCompanionTargets(companionCombatant, combatSystem);

      if (adjacentTargets.length > 0 && companionCombatant.ap >= 2) {
        // Attack lowest HP target
        adjacentTargets.sort((a, b) => a.hp - b.hp);
        const target = adjacentTargets[0];
        const result = combatSystem.attack(companionCombatant, target, 'basic');
        actions.push({ type: 'attack', result, target });

        if (combatSystem.state === 'victory' || combatSystem.state === 'defeat') break;
      } else if (companionCombatant.ap >= 1) {
        // Move toward nearest enemy
        const moved = this._companionMoveTowardEnemy(companionCombatant, combatSystem);
        if (moved) {
          actions.push({ type: 'move', x: companionCombatant.gridX, y: companionCombatant.gridY });
        } else {
          break;
        }
      } else {
        break;
      }
    }

    return actions;
  }

  /**
   * Get targets for a companion (enemies).
   * @param {object} companion
   * @param {object} combatSystem
   * @returns {object[]}
   */
  _getCompanionTargets(companion, combatSystem) {
    const targets = [];
    const maxRange = companion.behavior === 'ranged' ? 6 : 2;

    for (let r = 0; r < combatSystem.rows; r++) {
      for (let c = 0; c < combatSystem.cols; c++) {
        const dist = Math.abs(c - companion.gridX) + Math.abs(r - companion.gridY);
        if (dist > 0 && dist <= maxRange) {
          const occupant = combatSystem.grid[r][c];
          if (occupant && occupant.alive && occupant.type === 'enemy') {
            targets.push(occupant);
          }
        }
      }
    }

    return targets;
  }

  /**
   * Move companion one step toward nearest enemy.
   * @param {object} companion
   * @param {object} combatSystem
   * @returns {boolean}
   */
  _companionMoveTowardEnemy(companion, combatSystem) {
    let nearest = null;
    let nearestDist = Infinity;

    for (const enemy of combatSystem.enemies) {
      if (!enemy.alive) continue;
      const dist = Math.abs(enemy.gridX - companion.gridX) + Math.abs(enemy.gridY - companion.gridY);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = enemy;
      }
    }

    if (!nearest) return false;

    const directions = [
      { dx: 0, dy: -1 }, { dx: 0, dy: 1 },
      { dx: -1, dy: 0 }, { dx: 1, dy: 0 },
    ];

    let bestDir = null;
    let bestDist = nearestDist;

    for (const { dx, dy } of directions) {
      const nx = companion.gridX + dx;
      const ny = companion.gridY + dy;
      if (nx < 0 || nx >= combatSystem.cols || ny < 0 || ny >= combatSystem.rows) continue;
      if (combatSystem.grid[ny][nx] !== null) continue;

      const dist = Math.abs(nearest.gridX - nx) + Math.abs(nearest.gridY - ny);
      if (dist < bestDist) {
        bestDist = dist;
        bestDir = { dx, dy };
      }
    }

    if (bestDir) {
      const result = combatSystem.moveUnit(companion, companion.gridX + bestDir.dx, companion.gridY + bestDir.dy);
      return result.success;
    }

    return false;
  }

  // ── Internal Helpers ──────────────────────────────────

  /**
   * Create a companion instance from a definition.
   * @param {object} def - Companion definition from JSON
   * @param {object} character - Owner character
   * @param {number} level - Scaling level
   * @returns {object}
   */
  _createCompanionInstance(def, character, level) {
    const scaling = def.scalingPerLevel || { hp: 0, damage: 0, defense: 0, speed: 0 };
    const effectiveLevel = Math.max(1, level);

    return {
      id: `${def.id}_${character.id}`,
      definitionId: def.id,
      name: def.name,
      sourceType: def.type,
      sourceId: def.sourceId,
      ownerId: character.id,
      ownerName: character.name,
      behavior: def.behavior || 'melee',
      level: effectiveLevel,
      hp: def.stats.hp + scaling.hp * (effectiveLevel - 1),
      maxHp: def.stats.hp + scaling.hp * (effectiveLevel - 1),
      damage: def.stats.damage + scaling.damage * (effectiveLevel - 1),
      defense: def.stats.defense + scaling.defense * (effectiveLevel - 1),
      speed: def.stats.speed + scaling.speed * (effectiveLevel - 1),
      abilities: def.abilities || [],
      description: def.description || '',
    };
  }

  /**
   * Sync active companions to party.companions array.
   */
  _syncToParty() {
    if (!this._party) return;
    this._party.companions = this.getPartyCompanions();
  }

  /**
   * Reset the companion system.
   */
  reset() {
    this._activeCompanions.clear();
    if (this._party) {
      this._party.companions = [];
    }
  }
}

/** Singleton instance. */
const companionSystem = new CompanionSystem();
export default companionSystem;
export { CompanionSystem, MAX_COMPANIONS_PER_CHARACTER, MAX_PARTY_COMPANIONS };
